import { useState } from "react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Pencil, Plus, RotateCcw, X } from "lucide-react";
import { useSetores } from "@/hooks/use-setores";
import { useVinculosAtivos, useFuncionarios } from "@/hooks/use-equipe";
import type { FuncionarioSetor } from "@shared/schema";

export function hojeISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function fmtData(iso: string | null | undefined): string {
  if (!iso) return "";
  const [a, m, d] = iso.split("-");
  return `${d}/${m}/${a}`;
}

const fmtHa = new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const fmtInt = new Intl.NumberFormat("pt-BR");

/**
 * Item da lista de vínculos ABERTOS enquanto o formulário está na tela.
 * Com `id` = já existe no banco; sem `id` = criado nesta edição, ainda não gravado.
 */
export interface VinculoForm {
  id?: string;
  setorId: string;
  inicio: string;
}

interface Props {
  /** undefined = funcionário ainda não existe (cadastro novo). */
  funcionarioId?: string;
  /** Estado DESEJADO dos vínculos abertos — vive no formulário do pai. */
  vinculos: VinculoForm[];
  onChange: (v: VinculoForm[]) => void;
  /** O que está gravado no banco hoje — base do diff "vai encerrar ao salvar". */
  persistidosAbertos: FuncionarioSetor[];
  /** Vigências já encerradas (só leitura). */
  historico: FuncionarioSetor[];
  carregando?: boolean;
  /** false quando o perfil não é Encarregado: dá para tirar setor, não para pôr. */
  podeVincular: boolean;
}

/**
 * Setores do encarregado — **puro estado de formulário, nenhuma chamada de API**.
 *
 * Antes, "Vincular" e o "×" gravavam na hora. Isso quebrava três coisas:
 *  1. não dava para virar Encarregado direto de Diarista — o servidor lia o regime
 *     PERSISTIDO, ainda DIARISTA, e recusava com "Só mensalista pode ser encarregado
 *     de setor". Só funcionava salvando como Mensalista, reabrindo e vinculando;
 *  2. Cancelar não desfazia o vínculo — ele já estava gravado;
 *  3. era impossível cadastrar alguém já como Encarregado, porque vincular setor
 *     exige um `id` que ainda não existia.
 *
 * Agora tudo isso é lista em memória, gravada junto com o funcionário num único
 * Salvar (uma transação no servidor — ver I15 em shared/schema.ts).
 */
export function SetoresDoEncarregado({
  funcionarioId, vinculos, onChange, persistidosAbertos, historico, carregando, podeVincular,
}: Props) {
  const { setores = [] } = useSetores();
  const { data: todosAtivos = [] } = useVinculosAtivos();
  const { data: funcionarios = [] } = useFuncionarios(true);

  const [setorSelecionado, setSetorSelecionado] = useState("");
  const [inicio, setInicio] = useState(hojeISO());
  const [editandoData, setEditandoData] = useState<string | null>(null);

  const setorDe = (id: string) => setores.filter(s => s.id === id)[0];
  const nomeSetor = (id: string) => setorDe(id)?.nome ?? id;
  const nomeFuncionario = (id: string) => {
    const f = funcionarios.filter(x => x.id === id)[0];
    return f?.apelido ?? f?.nome ?? "outro encarregado";
  };

  // Chave estável por item: o `id` do banco, ou o setor para os novos (o mesmo
  // setor não pode aparecer duas vezes na lista, então não colide).
  const chaveDe = (v: VinculoForm) => v.id ?? `novo:${v.setorId}`;

  const jaNaLista = new Set(vinculos.map(v => v.setorId));
  const disponiveis = setores.filter(s => !jaNaLista.has(s.id));

  // O que vai ser ENCERRADO ao salvar: estava aberto no banco e saiu da lista.
  const idsDesejados = new Set(vinculos.filter(v => v.id).map(v => v.id as string));
  const aEncerrar = persistidosAbertos.filter(p => !idsDesejados.has(p.id));

  /** Os OUTROS encarregados ativos do setor — a dupla, mostrada como informação. */
  const parceirosNoSetor = (setorId: string) =>
    todosAtivos
      .filter(v => v.setorId === setorId && v.funcionarioId !== funcionarioId && !v.fim)
      .map(v => nomeFuncionario(v.funcionarioId));

  const totalHa = vinculos.reduce((s, v) => s + Number(setorDe(v.setorId)?.areaHectares ?? 0), 0);
  const totalPlantas = vinculos.reduce((s, v) => s + Number(setorDe(v.setorId)?.quantidadeMudas ?? 0), 0);

  const vincular = () => {
    if (!setorSelecionado || !inicio) return;
    onChange(vinculos.concat([{ setorId: setorSelecionado, inicio }]));
    setSetorSelecionado("");
    setInicio(hojeISO());
  };

  const remover = (v: VinculoForm) =>
    onChange(vinculos.filter(x => chaveDe(x) !== chaveDe(v)));

  const desfazerRemocao = (p: FuncionarioSetor) =>
    onChange(vinculos.concat([{ id: p.id, setorId: p.setorId, inicio: p.inicio }]));

  const mudarInicio = (v: VinculoForm, novo: string) =>
    onChange(vinculos.map(x => (chaveDe(x) === chaveDe(v) ? { ...x, inicio: novo } : x)));

  return (
    <div className="rounded-lg border p-3 space-y-3">
      <Label>Setores do encarregado</Label>

      {carregando ? (
        <p className="text-xs text-muted-foreground">Carregando vínculos...</p>
      ) : vinculos.length === 0 ? (
        <p className="text-xs text-muted-foreground">Nenhum setor vinculado ainda.</p>
      ) : (
        <div className="space-y-1.5">
          {vinculos.map(v => {
            const setor = setorDe(v.setorId);
            const parceiros = parceirosNoSetor(v.setorId);
            const emEdicao = editandoData === chaveDe(v);
            return (
              <div key={chaveDe(v)} className="rounded-lg bg-muted border border-border px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {nomeSetor(v.setorId)}
                      {!v.id && (
                        <span className="ml-1.5 text-[10px] font-normal text-muted-foreground">• novo</span>
                      )}
                    </p>
                    {/* O tamanho da responsabilidade, não só o nome do setor. */}
                    <p className="text-[11px] text-muted-foreground">
                      {setor?.areaHectares ? `${fmtHa.format(Number(setor.areaHectares))} ha` : "sem área"}
                      {" · "}
                      {setor?.quantidadeMudas ? `${fmtInt.format(setor.quantidadeMudas)} plantas` : "sem plantas"}
                    </p>
                    {!emEdicao && <p className="text-[11px] text-muted-foreground">Desde {fmtData(v.inicio)}</p>}
                    {parceiros.length > 0 && (
                      <p className="text-[11px] text-muted-foreground/80 truncate">também: {parceiros.join(", ")}</p>
                    )}
                  </div>
                  {!emEdicao && (
                    <div className="flex shrink-0">
                      <Button
                        size="icon"
                        variant="ghost"
                        title="Corrigir a data de entrada"
                        onClick={() => setEditandoData(chaveDe(v))}
                      >
                        <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        title={v.id ? "Encerrar a vigência ao salvar" : "Tirar da lista"}
                        onClick={() => remover(v)}
                      >
                        <X className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    </div>
                  )}
                </div>

                {emEdicao && (
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-[11px] text-muted-foreground shrink-0">Desde</span>
                    <input
                      type="date"
                      value={v.inicio}
                      onChange={e => mudarInicio(v, e.target.value)}
                      className="min-w-0 flex-1 rounded-lg border border-border px-2 py-1 text-sm bg-card"
                    />
                    <Button size="icon" variant="ghost" title="Pronto" onClick={() => setEditandoData(null)}>
                      <Check className="h-4 w-4 text-sucesso" />
                    </Button>
                  </div>
                )}
              </div>
            );
          })}

          {/* Rodapé: o total da responsabilidade. */}
          <p className="pt-0.5 text-[11px] font-medium text-muted-foreground">
            {vinculos.length} {vinculos.length === 1 ? "setor" : "setores"}
            {" · "}{fmtHa.format(totalHa)} ha
            {" · "}{fmtInt.format(totalPlantas)} plantas
          </p>
        </div>
      )}

      {/* Encerrar é irreversível depois do Salvar — mostra o que vai acontecer,
          com desfazer, enquanto ainda dá tempo. */}
      {aEncerrar.length > 0 && (
        <div className="rounded-lg border border-atencao/30 bg-atencao-suave p-2.5 space-y-1.5">
          <p className="text-[11px] font-medium text-atencao">Ao salvar, encerra a vigência em:</p>
          {aEncerrar.map(p => (
            <div key={p.id} className="flex items-center justify-between gap-2">
              <span className="text-xs text-atencao truncate">
                {nomeSetor(p.setorId)} <span className="text-atencao">(desde {fmtData(p.inicio)})</span>
              </span>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 shrink-0 text-atencao"
                onClick={() => desfazerRemocao(p)}
              >
                <RotateCcw className="h-3.5 w-3.5 mr-1" />Desfazer
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Atribuir setor: no celular o campo de data não pode espremer o botão,
          por isso o select fica numa linha e data + Vincular na de baixo. */}
      {podeVincular && (
        <div className="space-y-2">
          <select
            value={setorSelecionado}
            onChange={e => setSetorSelecionado(e.target.value)}
            className="w-full rounded-lg border border-border px-3 py-2 text-sm bg-card"
          >
            <option value="">Escolher setor...</option>
            {disponiveis.map(s => (
              <option key={s.id} value={s.id}>{s.nome}</option>
            ))}
          </select>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground shrink-0">Desde</span>
            <input
              type="date"
              value={inicio}
              onChange={e => setInicio(e.target.value)}
              className="min-w-0 flex-1 rounded-lg border border-border px-2 py-2 text-sm bg-card"
            />
            <Button
              type="button"
              size="sm"
              className="shrink-0"
              disabled={!setorSelecionado || !inicio}
              onClick={vincular}
            >
              <Plus className="h-4 w-4 mr-1" />Vincular
            </Button>
          </div>
        </div>
      )}

      <p className="text-[11px] text-muted-foreground">
        Os setores são gravados junto com o funcionário quando você clicar em <strong>Salvar</strong>.
        Cancelar descarta tudo.
      </p>

      {historico.length > 0 && (
        <div className="space-y-1 pt-1">
          <p className="text-[11px] font-medium text-muted-foreground uppercase">Histórico</p>
          {historico.map(v => (
            <div key={v.id} className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
              <span className="truncate">{nomeSetor(v.setorId)}</span>
              <Badge variant="outline" className="text-[10px] shrink-0">
                {fmtData(v.inicio)} → {fmtData(v.fim)}
              </Badge>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
